import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addDoc, collection, deleteDoc, doc, serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { db, storage } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { notifyFollowersOfDrop } from "../../services/notificationService";
import { fetchCreatorDrops } from "../../services/creatorStudioService";
import { contentTypeLabel } from "./creatorHelpers";
import CreatorStudioLayout from "./CreatorStudioLayout";

function isScheduledDrop(drop) {
  if (!drop?.date) return false;
  const release = new Date(`${drop.date}T${drop.time || "00:00"}`);
  return !Number.isNaN(release.getTime()) && release > new Date();
}

function formatPublishDate(drop) {
  const d = new Date(`${drop.date}T${drop.time || "00:00"}`);
  return d.toLocaleString("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function ToggleRow({ title, desc, value, onChange }) {
  return (
    <div className="cp-toggle-row">
      <div>
        <div className="cp-tr-title">{title}</div>
        <div className="cp-tr-desc">{desc}</div>
      </div>
      <button type="button" className={`cp-toggle${value ? " on" : ""}`} onClick={onChange} aria-pressed={value} />
    </div>
  );
}

export default function ScheduleDrop() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [scheduledDrops, setScheduledDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [notifyFollowers, setNotifyFollowers] = useState(true);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState("");

  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  }, []);

  const showToast = (text, type = "success") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const drops = await fetchCreatorDrops(currentUser.uid);
        if (cancelled) return;
        setScheduledDrops(drops.filter(isScheduledDrop).sort((a, b) => {
          const ta = new Date(`${a.date}T${a.time || "00:00"}`).getTime();
          const tb = new Date(`${b.date}T${b.time || "00:00"}`).getTime();
          return ta - tb;
        }));
      } catch (err) {
        console.error("Load scheduled drops error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  const reloadScheduled = async () => {
    if (!currentUser?.uid) return;
    const drops = await fetchCreatorDrops(currentUser.uid);
    setScheduledDrops(drops.filter(isScheduledDrop).sort((a, b) => {
      const ta = new Date(`${a.date}T${a.time || "00:00"}`).getTime();
      const tb = new Date(`${b.date}T${b.time || "00:00"}`).getTime();
      return ta - tb;
    }));
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setDate("");
    setTime("09:00");
    setNotifyFollowers(true);
    setMediaFile(null);
    setMediaPreview("");
    setShowForm(false);
  };

  const handleMediaChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
  };

  const handleSchedule = async () => {
    if (!currentUser) return;
    if (!title.trim()) {
      showToast("Drop title is required.", "error");
      return;
    }
    if (!date) {
      showToast("Please choose a release date.", "error");
      return;
    }

    try {
      setSaving(true);
      let mediaUrl = "";
      if (mediaFile) {
        const safeName = mediaFile.name.replace(/\s+/g, "-").toLowerCase();
        const path = `drops/${currentUser.uid}/${Date.now()}-${safeName}`;
        const fileRef = ref(storage, path);
        await uploadBytes(fileRef, mediaFile);
        mediaUrl = await getDownloadURL(fileRef);
      }

      const dropRef = await addDoc(collection(db, "drops"), {
        creatorId: currentUser.uid,
        creatorUsername: userProfile?.handle || userProfile?.username || "creator",
        creatorDisplayName: userProfile?.displayName || userProfile?.username || "Creator",
        creatorAvatar: userProfile?.avatar || userProfile?.avatarUrl || "",
        title: title.trim(),
        description: description.trim(),
        mediaUrl,
        thumbnailUrl: mediaUrl || "",
        date,
        time,
        status: "scheduled",
        notifyFollowers,
        active: true,
        viewCount: 0,
        maxViews: 100,
        createdAt: serverTimestamp(),
      });

      if (notifyFollowers) {
        notifyFollowersOfDrop({
          creatorId: currentUser.uid,
          creatorDisplayName: userProfile?.displayName || "Creator",
          creatorUsername: userProfile?.handle || userProfile?.username || "creator",
          creatorAvatar: userProfile?.avatar || "",
          dropId: dropRef.id,
          dropTitle: title.trim(),
          viewLimit: 100,
        }).catch(console.warn);
      }

      showToast("Drop scheduled!");
      resetForm();
      await reloadScheduled();
    } catch (err) {
      console.error("Schedule drop error:", err);
      showToast("Could not schedule drop.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (dropId) => {
    try {
      await deleteDoc(doc(db, "drops", dropId));
      setScheduledDrops((prev) => prev.filter((d) => d.id !== dropId));
      showToast("Scheduled drop cancelled.");
    } catch {
      showToast("Could not cancel drop.", "error");
    }
  };

  return (
    <CreatorStudioLayout activePath="/schedule-drop">
      {toast && (
        <div className={`cp-toast cp-toast-${toast.type === "error" ? "error" : "success"}`}>
          {toast.text}
        </div>
      )}

      <div className="cp-page-header">
        <div>
          <div className="cs-page-eyebrow">Drops</div>
          <h1 className="cp-section-title">Scheduled <em>Drops</em></h1>
          <span className="cp-section-hand">
            {scheduledDrops.length
              ? `${scheduledDrops.length} drop${scheduledDrops.length === 1 ? "" : "s"} queued and ready`
              : "queue your next release for a specific date and time"}
          </span>
        </div>
        {!showForm && (
          <button type="button" className="cp-btn cp-btn-gold" onClick={() => setShowForm(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Schedule drop
          </button>
        )}
      </div>

      {loading ? (
        <div className="cs-loading">Loading scheduled drops…</div>
      ) : scheduledDrops.length === 0 && !showForm ? (
        <div className="cp-empty">
          <p>No scheduled drops yet.</p>
          <button type="button" className="cp-btn cp-btn-gold" style={{ marginTop: 16 }} onClick={() => setShowForm(true)}>
            Schedule your first drop
          </button>
        </div>
      ) : (
        scheduledDrops.map((drop) => {
          const thumb = drop.thumbnailUrl || drop.mediaUrl;
          const price = drop.price || drop.unlockPrice || "Free";
          return (
            <div key={drop.id} className="cp-drop-row scheduled">
              {thumb ? (
                <img className="cp-dr-thumb" src={thumb} alt="" />
              ) : (
                <div className="cp-dr-thumb cp-dr-thumb-empty">No image</div>
              )}
              <div className="cp-dr-info">
                <div className="cp-dr-title">{drop.title || "Untitled drop"}</div>
                <div className="cp-dr-meta">
                  <span className="cp-dr-pill cp-drp-scheduled">Scheduled</span>
                  <span className="cp-dr-type">{contentTypeLabel(drop.contentType)}</span>
                </div>
                <div className="cp-dr-publish">🕐 Publishes {formatPublishDate(drop)}</div>
              </div>
              <div className="cp-dr-stats">
                <div className="cp-drs">
                  <div className="cp-drs-num gold">{typeof price === "number" ? `$${price}` : price}</div>
                  <div className="cp-drs-lbl">Price</div>
                </div>
                <div className="cp-drs">
                  <div className="cp-drs-num gold">100</div>
                  <div className="cp-drs-lbl">Views</div>
                </div>
              </div>
              <div className="cp-dr-actions">
                <button type="button" className="cp-btn cp-btn-ghost" onClick={() => navigate("/post")}>Edit</button>
                <button type="button" className="cp-btn cp-btn-danger" onClick={() => handleCancel(drop.id)}>Cancel</button>
              </div>
            </div>
          );
        })
      )}

      {showForm && (
        <div className="cp-drop-form">
          <div className="cp-df-section">
            <div className="cp-df-section-title">
              <span className="cp-df-section-num">1</span>
              Drop details
            </div>
            <div className="cp-field">
              <label className="cp-field-label">Drop title</label>
              <input className="cp-field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Morning Ritual Vol. 5 — Back to Basics" maxLength={80} />
            </div>
            <div className="cp-field">
              <label className="cp-field-label">Description</label>
              <textarea className="cp-field-textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Give your supporters a teaser of what's coming…" maxLength={300} rows={3} />
            </div>
            <div className="cp-field">
              <label className="cp-field-label">Teaser image</label>
              <input type="file" accept="image/*" onChange={handleMediaChange} />
              {mediaPreview && (
                <img src={mediaPreview} alt="Preview" style={{ marginTop: 10, width: 120, height: 80, objectFit: "cover", borderRadius: 10 }} />
              )}
            </div>
          </div>

          <div className="cp-df-section">
            <div className="cp-df-section-title">
              <span className="cp-df-section-num">2</span>
              Release timing
            </div>
            <div className="cp-field-row">
              <div className="cp-field">
                <label className="cp-field-label">Release date</label>
                <input type="date" className="cp-field-input" value={date} min={tomorrow} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="cp-field">
                <label className="cp-field-label">Release time</label>
                <input type="time" className="cp-field-input" value={time} onChange={(e) => setTime(e.target.value)} />
              </div>
            </div>
            <span className="cp-field-hint">The 100-view window opens the moment the drop publishes — not before.</span>
          </div>

          <div className="cp-df-section">
            <div className="cp-df-section-title">
              <span className="cp-df-section-num">3</span>
              Options
            </div>
            <ToggleRow
              title="Notify followers"
              desc="Send a notification to all followers when the drop goes live"
              value={notifyFollowers}
              onChange={() => setNotifyFollowers((p) => !p)}
            />
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" className="cp-btn cp-btn-ghost" onClick={resetForm}>Cancel</button>
            <button type="button" className="cp-btn cp-btn-gold" onClick={handleSchedule} disabled={saving}>
              {saving ? "Scheduling…" : "Schedule drop"}
            </button>
          </div>
        </div>
      )}
    </CreatorStudioLayout>
  );
}
